var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
// External dependencies
import { useContext, useEffect, useState, memo, } from 'react';
import { Box, Typography, Grid, Autocomplete, } from '@mui/material';
import { useTranslation } from 'react-i18next';
import 'katex/dist/katex.min.css';
import { getAuthHeader, UserContext } from '../auth';
import { axiosClient } from '../axios';
import { LOG_LEVELS, SERVICES, ENDPOINTS } from '../constants';
import { TextFieldSG, ButtonSG } from '../components';
import { Field } from 'formik';
import { ACTION_TYPES, getSampleWorkflowPrompts } from './constants';
import { CustomWorkflowPrompt } from './CustomWorkflowPrompt';
import { workflowStyles } from './workflowStyles';
/**
 * Custom Prompt Action component
 * @param props Props
 * @returns Custom Prompt Action component
 */
export const CustomPromptAction = memo(function ({ action, actionIdx, actionErrors, actionTouched, setFieldValue, }) {
    // Get user credential
    const user = useContext(UserContext);
    // Get translation function
    const { t, i18n } = useTranslation();
    const SAMPLE_WORKFLOW_PROMPTS = getSampleWorkflowPrompts();
    const [workflowPrompts, setWorkflowPrompts] = useState([]);
    const [workflowPromptOption, setWorkflowPromptOption] = useState({ label: '', value: '' });
    const [workflowPromptsUpdatedFlag, setWorkflowPromptsUpdateFlag] = useState(0);
    /**
     * Update workflow prompts updated flag
     */
    const updateWorkflowPromptsUpdatedFlag = () => {
        setWorkflowPromptsUpdateFlag(workflowPromptsUpdatedFlag + 1);
    };
    // Fetch and update document when user credential changed
    useEffect(() => {
        const getWorkflowPrompts = () => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            try {
                // Get autheHeader
                const authHeader = yield getAuthHeader(user);
                // Get headers
                const headers = Object.assign(Object.assign({}, authHeader), { 'Accept-Language': i18n.language });
                // Get document
                const res = yield axiosClient.get(`${process.env.SERVER_URL}${ENDPOINTS.WORKFLOW}${ENDPOINTS.GET_WORKFLOW_ACTIONS}?type=${ACTION_TYPES.PROMPT}`, { headers });
                // Extract document
                const workflowActions = (_b = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.workflowActions;
                // Set prompts
                setWorkflowPrompts([...workflowActions, ...SAMPLE_WORKFLOW_PROMPTS]);
            }
            catch (err) {
                axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                    level: LOG_LEVELS.ERROR,
                    logMessage: `CustomPromptAction, useEffect, getWorkflowPrompts, ${err === null || err === void 0 ? void 0 : err.message}`,
                    metadata: {
                        env: process.env.ENV,
                        service: SERVICES.WORKFLOW,
                        userId: user === null || user === void 0 ? void 0 : user.uid,
                        email: user === null || user === void 0 ? void 0 : user.email,
                        userAgent: navigator.userAgent,
                    },
                });
            }
            ;
        });
        try {
            if (user) {
                getWorkflowPrompts();
            }
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `CustomPromptAction, useEffect, getWorkflowPrompts, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.WORKFLOW,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator.userAgent,
                },
            });
        }
        ;
    }, [user, workflowPromptsUpdatedFlag]);
    const /** Workflow Prompt Options */ workflowPromptOptions = workflowPrompts.map((workflowPrompt) => ({ label: (workflowPrompt === null || workflowPrompt === void 0 ? void 0 : workflowPrompt.name) || '', value: workflowPrompt === null || workflowPrompt === void 0 ? void 0 : workflowPrompt.promptContent }));
    const [
    /** Custom workflow prompt open */ customWorkflowPromptOpen, setCustomWorkflowPromptOpen] = useState(false);
    /**
     * Handle Save as custom click
     */
    const handleSaveAsCustomClick = () => {
        setCustomWorkflowPromptOpen(true);
    };
    return (_jsxs(Box, Object.assign({ sx: { width: '100%' } }, { children: [_jsx(Typography, Object.assign({ sx: Object.assign({}, workflowStyles.actionHeading1) }, { children: t('Custom / Default Prompt').toUpperCase() })), _jsx(Autocomplete, { value: {
                    label: workflowPromptOption === null || workflowPromptOption === void 0 ? void 0 : workflowPromptOption.label,
                    value: workflowPromptOption === null || workflowPromptOption === void 0 ? void 0 : workflowPromptOption.value
                }, isOptionEqualToValue: (option, value) => (option.value === value.value), onChange: (event, value) => {
                    setWorkflowPromptOption(value);
                    setFieldValue(`actions[${actionIdx}].promptContent`, value === null || value === void 0 ? void 0 : value.value);
                }, options: workflowPromptOptions, disableClearable: true, sx: { width: '100%', '& input': Object.assign({}, workflowStyles.actionInput), }, renderInput: (params) => _jsx(TextFieldSG, Object.assign({}, params)) }), _jsxs(Grid, Object.assign({ container: true, sx: { alignItems: 'center', margin: '0 0 0.5rem', } }, { children: [_jsx(Grid, Object.assign({ item: true }, { children: _jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, workflowStyles.actionHeading1), { margin: '0' }) }, { children: t('Prompt').toUpperCase() })) })), _jsx(Grid, Object.assign({ item: true }, { children: _jsx(Typography, Object.assign({ sx: Object.assign({}, workflowStyles.dot) }, { children: "\u00B7" })) })), _jsx(Grid, Object.assign({ item: true }, { children: _jsx(ButtonSG, Object.assign({ variant: 'text', onClick: handleSaveAsCustomClick, sx: Object.assign({}, workflowStyles.textUnderlineButton) }, { children: t('Save As Custom Prompt') })) }))] })), _jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, workflowStyles.actionHeading2), { color: 'greenLighter.main' }) }, { children: t('Your instruction to AI') })), _jsx(Field, { as: TextFieldSG, name: `actions[${actionIdx}].promptContent`, error: (actionTouched === null || actionTouched === void 0 ? void 0 : actionTouched.promptContent)
                    && Boolean(actionErrors === null || actionErrors === void 0 ? void 0 : actionErrors.promptContent), helperText: (actionTouched === null || actionTouched === void 0 ? void 0 : actionTouched.promptContent)
                    && (actionErrors === null || actionErrors === void 0 ? void 0 : actionErrors.promptContent), xstyles: {
                    padding: '0',
                    '& textarea': Object.assign({}, workflowStyles.actionInput),
                }, multiline: true, maxRows: 12 }), _jsx(CustomWorkflowPrompt, { customWorkflowPromptOpen: customWorkflowPromptOpen, prompt: action, updateWorkflowPromptsUpdatedFlag: updateWorkflowPromptsUpdatedFlag, setCustomWorkflowPromptOpen: setCustomWorkflowPromptOpen })] })));
});
